import { lighten, makeStyles, Theme } from '@material-ui/core/styles';
import { LoginLogoImg } from '@roc/feature-app-core';
import { PrintableArea } from '@roc/ui';

import { observer } from 'mobx-react';
import { forwardRef, useEffect, useState } from 'react';
import {
  formatCurrency,
  formatPercentage,
  getCompletePropertyAddress,
} from '@roc/ui/utils';
import { Box, Divider, Typography } from '@material-ui/core';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import {
  DateFormat,
  DUPLEX,
  formatBorrowerExperience,
  formatDate,
  getDaysBetween,
  insertIf,
  isEmptyString,
  LoanSubType,
  parseDate,
  PURCHASE,
  QUADRUPLEX,
  REFINANCE,
  SINGLE_FAMILY,
  TRIPLEX,
} from '@roc/feature-utils';
import {
  calculateThirdPartyCosts,
  generateLoanDetailsTableData,
  mapDownloadableQuoteData,
  mapDownloadableTermSheetData,
} from '../utils/summaryUtils';
import { visibility } from 'html2canvas/dist/types/css/property-descriptors/visibility';
import { useStore } from '@roc/client-portal-shared/hooks';
import { PropertyType } from '@roc/ui/formComponents';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontSize: '0.35in',
    fontFamily: 'sans-serif',
    letterSpacing: '-2px',
    paddingBottom: '2px',
  },
  light: {
    color: '#595959',
  },

  valuesTable: {
    borderCollapse: 'collapse',
    ['& td ']: {
      width: '20%',
      paddingLeft: '28px',
      paddingRigth: '28px',
      paddingTop: '4px',
      paddingBottom: '4px',
      lineHeight: '14px',
    },
    ['& tr td:nth-child(1), & tr td:nth-child(3), & tr td:nth-child(5)']: {
      color: '#595959',
    },
    ['& tr td:nth-child(2), & tr td:nth-child(4), & tr td:nth-child(6)']: {
      fontWeight: 'bold',
    },
  },

  closingCostsTable: {
    borderCollapse: 'collapse',
    ['& td ']: {
      paddingLeft: '28px',
      paddingRigth: '28px',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
    ['& tr td:nth-child(1)']: {
      fontWeight: 'normal',
    },
    ['& tr td:nth-child(2)']: {
      color: '#595959',
      fontWeight: 'bold',
    },
    ['& tr td:nth-child(3)']: {
      fontWeight: 'bold',
    },
    ['& tr td:nth-child(4)']: {
      color: '#595959',
      fontWeight: 'bold',
    },
    ['& [data-last=true] td']: {
      borderBottom: '1px solid #595959',
    },
    ['& [data-last=true] td:nth-child(1)']: {
      borderBottom: 0,
    },
  },

  closingCostsTableLine: {
    ['& td']: {
      borderBottom: '1px solid #595959',
    },
    ['& td:nth-child(1)']: {
      borderBottom: 0,
    },
  },

  economicsTable: {
    display: 'flex',
    fontSize: '0.12in',
    ['& > div']: {
      flex: 1,
      width: '40%',
      textWrap: 'nowrap',
      borderRight: '1px solid #656565',
      textAlign: 'center',
      padding: '10px',
      fontWeight: 'bold',
    },
    ['& > div:last-child']: {
      borderRight: 0,
    },
  },
  loanEconomicsValue: {
    color: 'black',
    paddingTop: '4px',
    fontSize: '0.2in',
    fontWeight: 'bold',
  },

  page: {
    color: 'black',
    fontSize: '0.12in',

    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'verdana, arial, sans-serif',
    lineHeight: '1',
    position: 'relative',

    ['& p']: {
      margin: '1em 0',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  table: {
    borderCollapse: 'collapse',
    ['& td']: {
      borderCollapse: 'collapse',
      padding: '5pt',
      border: '0.25pt solid ' + lighten(theme.palette.primary.dark, 0.5),
      width: '25%',
    },
    ['& th']: {
      color: 'black',
      textAlign: 'left',
      padding: '12px 0px',
    },
    ['& td:first-child']: {
      borderLeft: 0,
    },
    ['& td:last-child']: {
      borderRight: 0,
    },
  },
  tableInvis: {
    borderCollapse: 'collapse',
    ['& td']: {
      borderCollapse: 'collapse',
      padding: '5pt',
      border: 'none',
      width: '25%',
    },
    ['& th']: {
      color: 'black',
      textAlign: 'left',
      padding: '12px 0px',
    },
  },
  promoSubtitle: {
    fontSize: '16px',
    fontWeight: 700,
  },
  promoTitle: {
    color: '#285391',
    fontSize: '20px',
    fontWeight: 700,
    marginLeft: '10px',
  },
  promoBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(2, 136, 209, 0.08)',
    marginTop: '16px',
    padding: '16px',
  },
  thickRow: {
    borderTop: '2pt solid ' + lighten(theme.palette.primary.dark, 0.3),
  },
  header: {
    paddingBottom: '0.1in',
  },
  logoWrapper: {
    paddingRight: '8px',
  },
  logoContainer: {
    margin: 0,
  },
  headerLine: {
    display: 'flex',
    paddingBottom: '16px',
    //borderBottom: '4px solid ' + theme.palette.primary.main,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    justfyContent: 'flex-end',
    paddingTop: '0.2in',
  },

  bold: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    paddingTop: '6px',
    paddingBottom: '6px',
  },
  darkBackground: {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'black',
  },
  note: {
    fontSize: '6pt',
    color: 'rgb(102 102 102)',
  },
  section: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '4pt 0',
  },
  showOnPrint: {
    display: 'none',
  },
  hideOnPrint: {
    display: 'block',
  },
  protoTitleContainer: {
    display: 'flex',
  },
  print: {
    //A4 size page
    width: '8.27in',
    height: '11.69in',

    //Add paddings
    padding: '0 0.65in',
    ['& $header']: {
      paddingTop: '0.6in',
    },

    ['& $footer']: {
      paddingBottom: '0.6in',
    },

    //Display hidden elements
    ['& $showOnPrint']: {
      display: 'block',
    },
    ['& $hideOnPrint']: {
      display: 'none',
    },
  },
  freeAppraisal: {
    padding: '5px',
  },
}));

interface DownloadableQuoteProps {
  store: QuoteTypeStore;
}
interface DownloadableTermSheetProps {
  store: QuoteTypeStore;
  termSheetData: any;
  loan: any;
}

interface Props {
  data: any;
  isQuote?: boolean;
  loan?: any;
}

export const DownloadableQuote = observer(
  forwardRef<any, any>(({ store }: DownloadableQuoteProps, ref) => {
    const { globalStore } = useStore();
    const data = mapDownloadableQuoteData(store, globalStore);
    return <DownloadableQuoteComponent ref={ref} isQuote={true} data={data} />;
  })
);

export const DownloadableTermSheet = observer(
  forwardRef<any, any>(
    ({ termSheetData, loan }: DownloadableTermSheetProps, ref) => {
      const { globalStore } = useStore();
      const data = mapDownloadableTermSheetData(
        termSheetData,
        globalStore,
        loan
      );
      return termSheetData ? (
        <DownloadableQuoteComponent ref={ref} isQuote={false} data={data} />
      ) : null;
    }
  )
);

const DownloadableQuoteComponent = observer(
  forwardRef<any, any>(({ data, isQuote = true }: Props, ref) => {
    const classes = useStyles();
    return (
      <PrintableArea printClass={classes.print} ref={ref}>
        {data?.properties?.length > 1 ? (
          <DownloadableQuoteMultiplePage isQuote={isQuote} data={data} />
        ) : (
          <DownloadableQuoteSinglePage isQuote={isQuote} data={data} />
        )}
      </PrintableArea>
    );
  })
);

const DownloadableQuoteMultiplePage = observer(
  ({ isQuote = true, data }: Props) => {
    const classes = useStyles();
    return (
      <>
        <div className={classes.page}>
          <Header isQuote={isQuote} data={data} />
          <Content>
            <SectionHeader>BORROWER INFORMATION</SectionHeader>
            <BorrowerInformation data={data} />
            <div style={{ paddingBottom: '30px' }}></div>
            <SectionHeader>CONDITIONAL TERMS</SectionHeader>
            <ConditionalTerms data={data} />
            {!!data.showFirstTimeBorrowerFreeAppraisalNotification && (
              <p className={classes.freeAppraisal}>
                You're eligible for a free appraisal on this loan! After
                submission, we'll send you a payment link, and the cost will be
                reimbursed at closing.
              </p>
            )}
            {!!data.showFreeAppraisalPromotionNotification && (
              <p className={classes.freeAppraisal}>
                You're eligible for a free appraisal on this loan!
              </p>
            )}
            {!!data.showFreeAppraisalNotificationRealValPromo && (
              <p className={classes.freeAppraisal}>
                You qualify for a free RealVal (Internal valuation) on this
                loan. The RealVal replaces the appraisal for a faster closing.
              </p>
            )}
            <div style={{ paddingBottom: '30px' }}></div>
            {!!data.brokerName && (
              <>
                <SectionHeader>BROKER</SectionHeader>
                <BrokerInformation data={data} />
                <div style={{ paddingBottom: '30px' }}></div>
              </>
            )}
          </Content>
          <Footer className={classes.showOnPrint} />
        </div>
        <PropertiesInformation isQuote={isQuote} data={data} />
        <div className={classes.page}>
          <Header
            className={classes.showOnPrint}
            isQuote={isQuote}
            data={data}
          />
          <Content>
            <SectionHeader>ESTIMATED CLOSING COSTS</SectionHeader>
            <EstimatedClosingCosts data={data} />
          </Content>
          <Footer />
        </div>
      </>
    );
  }
);

export const NotificationContent = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.promoBanner}>
      <div className={classes.protoTitleContainer}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M10.6602 8.89L11.6002 12H14.4202L12.1502 13.62L13.0802 16.63L10.6602 14.79L8.24016 16.63L9.17016 13.62L6.90016 12H9.72016L10.6602 8.89ZM8.24016 10H0.660156L6.83016 14.41L4.49016 22L10.6602 17.31L16.8402 22L14.4902 14.41L20.6602 10H13.0802L10.6602 2L8.24016 10ZM21.0202 22L19.1602 15.99L23.3402 13H19.9002L16.8202 15.2L18.2802 19.92L21.0202 22ZM16.6602 8L14.8402 2L13.8002 5.45L14.5702 8H16.6602Z"
            fill="#2E62FF"
          />
        </svg>
        <span className={classes.promoTitle}>{title}</span>
      </div>
      <div className={classes.promoSubtitle}>{subtitle}</div>
    </div>
  );
};
const DownloadableQuoteSinglePage = observer(
  ({ isQuote = true, data }: Props) => {
    const classes = useStyles();
    const properties: any[] = data.properties;

    const borrowersCount = data?.borrowers?.length || 0;
    const splitInPages =
      borrowersCount > 1 ||
      !!data.brokerName ||
      !!data.showFirstTimeBorrowerFreeAppraisalNotification ||
      !!data.showFreeAppraisalPromotionNotification ||
      data.loanSubtype === LoanSubType.GROUND_UP;

    return (
      <>
        <div className={classes.page}>
          <Header isQuote={isQuote} data={data} />
          <Content>
            <SectionHeader>BORROWER INFORMATION</SectionHeader>
            <BorrowerInformation data={data} />
            <div style={{ paddingBottom: '10px' }}></div>
            <SectionHeader>CONDITIONAL TERMS</SectionHeader>
            <ConditionalTerms data={data} />
            {data.showFirstTimeBorrowerFreeAppraisalNotification && (
              <NotificationContent
                title="First-time Borrower Benefit"
                subtitle="Appraisal Fee Waived"
              />
            )}
            {data.showFreeAppraisalPromotionNotification && (
              <NotificationContent
                title="Free Appraisal Promotion"
                subtitle="Appraisal Fee Waived"
              />
            )}
            <div style={{ paddingBottom: '10px' }}></div>
            {!!data.brokerName && (
              <>
                <SectionHeader>BROKER</SectionHeader>
                <BrokerInformation data={data} />
                <div style={{ paddingBottom: '30px' }}></div>
              </>
            )}
          </Content>
          {splitInPages && <Footer className={classes.showOnPrint} />}
          {!splitInPages && (
            <>
              {properties.map(property => (
                <Box paddingBottom={2}>
                  <SectionHeader>PROPERTY INFORMATION</SectionHeader>
                  <PropertyInformation
                    property={property}
                    data={data}
                    isQuote={isQuote}
                  />
                </Box>
              ))}
              <Content>
                <SectionHeader>ESTIMATED CLOSING COSTS</SectionHeader>
                <EstimatedClosingCosts data={data} />
              </Content>
              <Footer />
            </>
          )}
        </div>
        {splitInPages && (
          <div className={classes.page}>
            <Header
              isQuote={isQuote}
              data={data}
              className={classes.showOnPrint}
            />
            {properties.map(property => (
              <Box paddingBottom={2}>
                <SectionHeader>PROPERTY INFORMATION</SectionHeader>
                <PropertyInformation
                  property={property}
                  data={data}
                  isQuote={isQuote}
                />
              </Box>
            ))}
            <Content>
              <SectionHeader>ESTIMATED CLOSING COSTS</SectionHeader>
              <EstimatedClosingCosts data={data} />
            </Content>
            <Footer />
          </div>
        )}
      </>
    );
  }
);

const BorrowerInformation = ({ data }: Props) => {
  const classes = useStyles();
  const borrowers = data.borrowers;
  const entityName = data.entity?.entityName;

  return (
    <table className={classes.valuesTable}>
      {borrowers.map(borrower => (
        <>
          {borrowers.length > 1 && (
            <tr>
              <td colSpan={1}>Name</td>
              <td colSpan={3}>
                {borrower.firstName + ' ' + borrower.lastName}
              </td>
            </tr>
          )}
          <tr>
            <td>Estimated FICO Score</td>
            <td>{borrower.ficoScore}</td>
            <td>Experience Score</td>
            <td>{formatBorrowerExperience(borrower.experience)}</td>
          </tr>
        </>
      ))}
      {!!entityName && (
        <tr>
          <td>Entity Name</td>
          <td>{entityName}</td>
        </tr>
      )}
    </table>
  );
};

const BrokerInformation = ({ data }: Props) => {
  const classes = useStyles();

  return (
    <table className={classes.valuesTable}>
      <tr>
        <td>Broker Name</td>
        <td>{data.brokerName}</td>
        <td>Broker Fees</td>
        <td>{data.brokerFee}</td>
      </tr>
    </table>
  );
};

const ConditionalTerms = ({ data }: Props) => {
  const classes = useStyles();
  const outputs = data.outputs;
  const economics = data.economics;
  const showConstructionHoldback =
    data.loanSubtype === LoanSubType.GROUND_UP ||
    data.loanSubtype === LoanSubType.FIX_AND_FLIP;
  const isStabilizedBridge = data.loanSubtype === LoanSubType.STABILIZED_BRIDGE;
  const moreThan180DaysOwned = data.properties.some(
    p =>
      p.purchaseDate &&
      getDaysBetween(new Date(), parseDate(p.purchaseDate)) > 180
  );

  const ltcCatchupDraw =
    data?.ltcCatchupDraw ?? outputs?.ltcCatchupDrawAmount ?? 0;

  const termsTable =
    data.loanSubtype === LoanSubType.STABILIZED_BRIDGE ? (
      <>
        <tr>
          <td>Loan Purpose</td>
          <td>{data.loanPurpose}</td>
          <td>Interest Payment Type</td>
          <td>{data.interestPaymentType}</td>
        </tr>
        <tr>
          {moreThan180DaysOwned ? (
            <>
              <td>LTC</td>
              <td>{formatPercentage(data.ltv)}</td>
            </>
          ) : (
            <>
              <td>LTV</td>
              <td>{formatPercentage(data.economics.loanToCost)}</td>
            </>
          )}
          <td>Interest Reserve</td>
          <td>{formatCurrency(outputs.interestReserve)}</td>
        </tr>
      </>
    ) : (
      <>
        <tr>
          <td>Loan Purpose</td>
          <td>{data.loanPurpose}</td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>{'Initial Advance'}</td>
          <td>{formatCurrency(outputs.initialLoanAmount)}</td>
          <td>Interest Payment Type</td>
          <td>{data.interestPaymentType}</td>
        </tr>
        <tr>
          {showConstructionHoldback && (
            <>
              <td>Construction Holdback</td>
              <td>
                {formatCurrency(outputs.constructionHoldback - ltcCatchupDraw)}
              </td>
            </>
          )}
          <td>{`${
            economics.interestReserveFinanced
              ? 'Financed Interest Reserve:'
              : 'Interest Reserve:'
          }`}</td>
          <td>{formatCurrency(outputs.interestReserve)}</td>
        </tr>
        <tr>
          <td>ARLTV</td>
          <td>{formatPercentage(data.arltv)}</td>
          <td>LTFC</td>
          <td>{formatPercentage(data.ltfc)}</td>
        </tr>
        {data.loanSubtype === LoanSubType.GROUND_UP && (
          <tr>
            <td>Financed IR</td>
            <td>{economics.interestReserveFinanced ? 'Yes' : 'No'}</td>
            <td>LTC Catchup </td>
            <td>{formatCurrency(ltcCatchupDraw)}</td>
          </tr>
        )}
        <tr>
          <td>Construction Holdback Rate</td>
          <td>
            {formatPercentage(
              outputs.constructionHoldbackToRenovationBudgetPercentage ?? 0
            )}
          </td>
          <td></td>
          <td></td>
        </tr>
      </>
    );

  return (
    <table className={classes.valuesTable}>
      <tr>
        <td style={{ padding: 0 }} colSpan={4}>
          <Box className={classes.economicsTable}>
            <div>
              <div className={classes.light}>Total Loan Amount</div>
              <div className={classes.loanEconomicsValue}>
                {formatCurrency(outputs.totalLoanAmount)}
              </div>
            </div>
            <div>
              <div className={classes.light}>Interest Rate</div>
              <div className={classes.loanEconomicsValue}>
                {formatPercentage(economics.interestRate)}
              </div>
            </div>
            <div>
              <div className={classes.light}>Duration</div>
              <div className={classes.loanEconomicsValue}>
                {economics.loanTermMonths + ' months'}
              </div>
            </div>
            <div>
              <div className={classes.light}>Initial LTC</div>

              <div className={classes.loanEconomicsValue}>
                {formatPercentage(economics.loanToCost)}
              </div>
            </div>
            <div>
              <div className={classes.light}>Borrower Cash to Close</div>
              <div className={classes.loanEconomicsValue}>
                {formatCurrency(outputs?.estimatedBorrowerCashToClose)}
              </div>
            </div>
          </Box>
        </td>
      </tr>
      {termsTable}
    </table>
  );
};

const PropertiesInformation = ({ isQuote, data }: Props) => {
  const classes = useStyles();
  const properties: any[] = data.properties;
  const showTotalRenovationBudget =
    data.loanSubtype !== LoanSubType.STABILIZED_BRIDGE;
  const showEstimatedAsIsValue =
    data.loanSubtype === LoanSubType.STABILIZED_BRIDGE;

  const splitInPages = (array, chunkSize) => {
    if (!array) return [];
    return array.reduce((result, item, index) => {
      const chunkIndex = Math.floor(index / chunkSize);
      if (!result[chunkIndex]) {
        result[chunkIndex] = [];
      }
      result[chunkIndex].push(item);
      return result;
    }, []);
  };

  const propertyPages = splitInPages(properties, 3);
  return (
    <>
      {propertyPages.map((chunk, pageIndex) => (
        <div className={classes.page} key={pageIndex}>
          {pageIndex === 0 && (
            <div className={classes.hideOnPrint}>
              <SectionHeader>PROPERTY INFORMATION</SectionHeader>
            </div>
          )}
          <Header
            isQuote={isQuote}
            className={classes.showOnPrint}
            data={data}
          />
          <Content>
            <Box paddingBottom={2}>
              <div className={classes.showOnPrint}>
                <SectionHeader>PROPERTY INFORMATION</SectionHeader>
              </div>
              {chunk.map(property => (
                <PropertyInformation
                  property={property}
                  multiProperty={properties.length > 1}
                  isQuote={isQuote}
                  data={data}
                />
              ))}
            </Box>
          </Content>
          <Footer className={classes.showOnPrint} />
        </div>
      ))}
    </>
  );
};

const PropertyInformation = ({
  property,
  multiProperty,
  data,
  isQuote,
}: Props & { property: any; multiProperty?: boolean }) => {
  const classes = useStyles();

  const isRefinance = REFINANCE === property.loanPurpose;
  const isPurchase = PURCHASE === property.loanPurpose;

  const isFixFlip = data.loanSubtype === LoanSubType.FIX_AND_FLIP;
  const isFixFlipPro = data.loanSubtype === LoanSubType.FIX_AND_FLIP_PRO;
  const isStabilizedBridge = data.loanSubtype === LoanSubType.STABILIZED_BRIDGE;
  const isGroundUp = data.loanSubtype === LoanSubType.GROUND_UP;

  const propertyAddress = {
    label: 'Property Address',
    value: property.address,
    columns: 2,
  };

  const emptySpace = { break: true, label: '', value: '' };

  const purchaseDate = {
    label: 'Purchase Date',
    value: formatDate(property.purchaseDate, DateFormat.MMDDYYYY),
  };

  const propertyType = {
    label: 'Property Type',
    value: property.propertyType,
  };

  const purchasePrice = {
    label: 'Purchase Price',
    value: formatCurrency(property.purchasePrice || 0),
  };

  const numberOfUnits = {
    label: 'Number of Units',
    value: {
      [SINGLE_FAMILY]: 1,
      [DUPLEX]: 2,
      [TRIPLEX]: 3,
      [QUADRUPLEX]: 4,
    }[property.propertyType],
  };

  const totalRenovationBudget = {
    label: 'Total Renovation Budget',
    value: formatCurrency(property.renovationBudget || 0),
  };

  const estimatedAsIsValue = {
    label: isStabilizedBridge
      ? 'Estimated As-Is Value*'
      : 'Estimated Site Value / As-Is Value',
    value: formatCurrency(property.currentMarketValue || 0),
  };

  const releaseAmount = {
    label: 'Release Amount',
    value: formatCurrency(property.releaseAmount || 0),
  };

  const propertyLoanAllocatedAmount = {
    label: 'Property Loan Allocated Amount',
    value: formatCurrency(property.propertyLoanAllocatedAmount || 0),
  };

  const completedRenoToDate = {
    label: 'Completed Reno to Date',
    value: formatCurrency(property.constructionRenoSpendToDate || 0),
  };

  const estimatedPayoffAmount = {
    label: 'Estimated Payoff Amount',
    value: formatCurrency(property.payoffAmount || 0),
  };

  const estimatedARV = {
    label: 'Estimated ARV',
    value: formatCurrency(property.marketValueAfterCompletion || 0),
  };

  const monthlyMarketRent = {
    label: 'Monthly Market Rent',
    value: formatCurrency(property.monthlyMarketRent || 0),
  };

  const annualTaxes = {
    label: 'Annual Taxes',
    value: formatCurrency(property.annualTaxes || 0),
  };

  const annualInsurance = {
    label: 'Annual Insurance',
    value: formatCurrency(property.annualInsurance || 0),
  };

  const permitsInPlace = {
    label: 'Permits',
    value: property.permitsInPlace ? 'Yes' : 'No',
  };

  const fixFlipTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      totalRenovationBudget,
      numberOfUnits,
      emptySpace,
      estimatedARV,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      totalRenovationBudget,
      estimatedARV,
      emptySpace,
      estimatedPayoffAmount,
    ]),
    ...insertIf(multiProperty, [releaseAmount, propertyLoanAllocatedAmount]),
  ];

  const groundUpTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      totalRenovationBudget,
      numberOfUnits,
      permitsInPlace,
      estimatedAsIsValue,
      emptySpace,
      estimatedARV,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      totalRenovationBudget,
      estimatedARV,
      permitsInPlace,
      estimatedPayoffAmount,
    ]),
    ...insertIf(multiProperty, [releaseAmount, propertyLoanAllocatedAmount]),
  ];

  const stabilizedBridgeTable = [
    propertyAddress,
    ...insertIf(isPurchase, [
      purchasePrice,
      propertyType,
      monthlyMarketRent,
      numberOfUnits,
      emptySpace,
      estimatedAsIsValue,
      emptySpace,
      annualTaxes,
      emptySpace,
      annualInsurance,
    ]),
    ...insertIf(isRefinance, [
      purchaseDate,
      propertyType,
      purchasePrice,
      numberOfUnits,
      completedRenoToDate,
      estimatedAsIsValue,
      monthlyMarketRent,
      annualTaxes,
      emptySpace,
      annualInsurance,
    ]),
    ...insertIf(multiProperty, [releaseAmount, propertyLoanAllocatedAmount]),
  ];

  return (
    <Box paddingBottom={2}>
      <table className={classes.valuesTable} key={property.address}>
        {isFixFlipPro && <TableBuilder cells={[propertyAddress]} columns={2} />}
        {isFixFlip && <TableBuilder cells={fixFlipTable} columns={2} />}
        {isGroundUp && <TableBuilder cells={groundUpTable} columns={2} />}
        {isStabilizedBridge && (
          <TableBuilder cells={stabilizedBridgeTable} columns={2} />
        )}
      </table>
    </Box>
  );
};

const EstimatedClosingCosts = ({ data }: Props) => {
  const classes = useStyles();
  const property = data.properties?.[0];
  const outputs = data.outputs;

  const thirdPartyCosts = calculateThirdPartyCosts(outputs.totalLoanAmount);
  const feesDetails = generateLoanDetailsTableData(
    outputs,
    thirdPartyCosts,
    property.loanPurpose,
    data.loanSubtype,
    data.brokerType
  );

  return (
    <table className={classes.valuesTable}>
      {feesDetails.categories.map(category =>
        category.fees.map((fee, i) => (
          <tr
            key={fee.name}
            className={
              i === category.fees.length - 1
                ? classes.closingCostsTableLine
                : ''
            }
          >
            <td>{i === 0 && category.name}</td>
            <td>{fee.name}</td>
            <td align="right">
              {fee.name != 'Title, Insurance and Other Estimated Costs'
                ? fee.amount
                : 'Contact your title agent'}
            </td>
            <td align="right">
              {i === category.fees.length - 1 && category.total}
            </td>
          </tr>
        ))
      )}
      <tr>
        <td></td>
        <td></td>
        <td align="right">TOTAL:</td>
        <td align="right">{feesDetails.total}</td>
      </tr>
    </table>
  );
};

const SectionHeader = ({ children }) => {
  return (
    <Box
      style={{
        fontSize: '20px',
        borderBottom: '1px solid #656565',
        paddingBottom: '2px',
        color: '#656565',
        marginBottom: '10px',
        paddingLeft: '8px',
      }}
    >
      {children}
    </Box>
  );
};

const Header = ({ className = '', isQuote, data }) => {
  const classes = useStyles();

  const titleByLoanSubtype = {
    [LoanSubType.FIX_AND_FLIP]: 'Fix And Flip',
    [LoanSubType.FIX_AND_FLIP_PRO]: 'Fix And Flip Pro',
    [LoanSubType.GROUND_UP]: 'Ground Up',
    [LoanSubType.STABILIZED_BRIDGE]: 'Stabilized Property',
  }[data.loanSubtype];

  return (
    <div className={`${classes.header} ${className}`}>
      <div className={classes.headerLine}>
        <div className={classes.logoWrapper}>
          <LoginLogoImg
            containerClassName={classes.logoContainer}
            imgStyle={{ height: '85px', padding: 0 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex' }}>
            <div className={classes.title} style={{ flex: 1 }}>
              {titleByLoanSubtype}{' '}
              <span style={{ fontWeight: 'bold', color: '#656565' }}>
                {isQuote ? 'Quote' : 'Term Sheet'}
              </span>
            </div>
            <Box>
              <Box pb={1} className={classes.light} fontWeight="bold">
                Date
              </Box>
              <div style={{ fontWeight: 'bold' }}>
                {formatDate(new Date(), DateFormat.MMDDYYYY)}
              </div>
            </Box>
          </div>

          {data.leverageException && (
            <Box pb={2}>
              <Typography
                style={{
                  fontStyle: 'italic',
                  fontSize: '16px',
                  color: '#595959',
                  margin: 0,
                }}
              >
                This quote is not pre approved and is subject to underwriting
                discretion
              </Typography>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

const Footer = ({ className = '' }) => {
  const classes = useStyles();
  return (
    <div className={`${classes.footer} ${className}`}>
      <p className={classes.note}>
        Please note this letter of interest serves to outline the terms of the
        proposed financing of the referenced transaction.This letter is merely a
        general proposal, and is neither a binding offer, nor a contract.
        Borrower understands that no such offer will be forthcoming prior to
        completion of appropriate due diligence and underwriting performed
        and/or contracted by Lender. This proposal does not create any legally
        binding obligations on any party hereto. All properties are subject to
        satisfactory Lender due diligence underwriting including: satisfactory
        appraisal, satisfactory credit review of the borrowing entity and Key
        Principals, and satisfactory review of the property’s market and
        submarket.
      </p>
    </div>
  );
};

const Content = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};

const TableBuilder = ({ cells, columns }) => {
  const rows = cells.reduce(
    (rows, cell) => {
      let currentRow = rows[rows.length - 1];
      const colsInCurrentRow = currentRow.reduce(
        (total, c) => (c.columns || 1) + total,
        0
      );
      const doesNotFit = colsInCurrentRow + (cell.columns || 1) > columns;
      if (cell.break || doesNotFit) {
        currentRow = [];
        rows.push(currentRow);
      }
      currentRow.push(cell);
      return rows;
    },
    [[]]
  );

  return rows.map(cells => (
    <tr>
      {cells.map(cell => (
        <>
          <td>{cell.label}</td>
          <td colSpan={cell.columns * 2 - 1}>{cell.value}</td>
        </>
      ))}
    </tr>
  ));
};
